import React, { useState, useEffect, useCallback } from 'react';
import './SubmissionsList.css';
import SrfIcons from './SrfIcons';
import { apiFetch } from '../services/api';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Box,
    Typography,
    IconButton,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useOri } from '../context/OriContext';
import LoadingIcon from './LoadingIcon';

export interface Submission {
    submittedBy: string;
    created: string;
    ori: string;
    /** Subject's name (LAST,FIRST) — present only when the response was an ident. */
    name?: string;
    stateSrf: string;
    type: string;
    stateOca: string;
    stateCaseType: string;
    ebts: string;
    face: string;
    state: string;
    device: string;
    updated: string | null;
    tcn: string;
    fbiSrf: string;
}

const SubmissionsList: React.FC = () => {
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [exclusiveStartKey, setExclusiveStartKey] = useState<object | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const { selectedOri, availableOris } = useOri();
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [includeAuthorized, setIncludeAuthorized] = useState(true);
    const navigate = useNavigate();

    // The merged view applies whenever the viewed agency is authorized for
    // others (e.g. a parent seeing its children) — for its own users and for
    // site admins alike; the server limits the merge to accessible agencies.
    const viewedAgency = availableOris.find(a => a.ori === selectedOri);
    const canToggleAuthorized = (viewedAgency?.authorized_oris?.length ?? 0) > 0;

    const fetchSubmissions = useCallback(async (startKey?: object | null, limit = rowsPerPage) => {
        if (!selectedOri) return;
        if (!startKey) {
            setIsLoading(true);
        }
        setError(null);

        try {
            const params = new URLSearchParams({ limit: String(limit), ori: selectedOri });
            if (!includeAuthorized) {
                params.append('includeAuthorized', 'false');
            }
            if (startKey) {
                params.append('exclusiveStartKey', JSON.stringify(startKey));
            }
            const path = `/api/risc/sub?${params.toString()}`;

            const response = await apiFetch(path);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to fetch submissions' }));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setSubmissions(prev => startKey ? [...prev, ...data.submissions] : data.submissions);
            setExclusiveStartKey(data.lastEvaluatedKey || null);
            setHasMore(!!data.lastEvaluatedKey);

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [rowsPerPage, selectedOri, includeAuthorized]);

    useEffect(() => {
        fetchSubmissions(null, rowsPerPage);
    }, [fetchSubmissions, rowsPerPage, selectedOri]);

    useEffect(() => {
        if (autoRefresh) {
            const intervalId = setInterval(() => {
                fetchSubmissions(null, rowsPerPage);
            }, 30000);
            return () => clearInterval(intervalId);
        }
    }, [autoRefresh, fetchSubmissions, rowsPerPage]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
        if (newPage * rowsPerPage >= submissions.length && hasMore) {
            fetchSubmissions(exclusiveStartKey);
        }
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const openDetails = (submission: Submission) => {
        navigate(`/submissions/${submission.tcn}`, {
            state: { from: 'submissions', ori: submission.ori || selectedOri, submission }
        });
    };

    return (
        <>
            <LoadingIcon isLoading={isLoading} showOverlay={true}/>
            <Paper sx={{ p: 2, m: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" gutterBottom component="div">
                        Recent Submissions
                    </Typography>
                    <Box>
                        <IconButton onClick={() => fetchSubmissions(null, rowsPerPage)} title="Refresh Submissions">
                            <Refresh />
                        </IconButton>
                        {canToggleAuthorized && (
                            <FormControlLabel
                                control={<Switch checked={includeAuthorized} onChange={(e) => { setIncludeAuthorized(e.target.checked); setPage(0); }} />}
                                label="Authorized agencies"
                                title="Include submissions from agencies your agency is authorized for"
                            />
                        )}
                        <FormControlLabel
                            control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
                            label="Auto-refresh"
                        />
                    </Box>
                </Box>
                {error && <p className="error-message">{error}</p>}
                {!isLoading && !error && (
                    <TableContainer>
                        <Table aria-label="submissions table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>TCN</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell className="hide-on-mobile">Agency</TableCell>
                                    <TableCell>FBI/State Match</TableCell>
                                    <TableCell className="hide-on-mobile">Status</TableCell>
                                    <TableCell className="hide-on-mobile">Date Submitted</TableCell>
                                    <TableCell className="hide-on-mobile">Submitted By</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(rowsPerPage > 0
                                    ? submissions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    : submissions
                                ).map((sub) => (
                                    <TableRow
                                        key={sub.tcn}
                                        hover
                                        onClick={() => openDetails(sub)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell className="mono-id">{sub.tcn}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{sub.name || ''}</TableCell>
                                        <TableCell className="hide-on-mobile mono-id">{sub.ori}</TableCell>
                                        <TableCell>
                                            <SrfIcons
                                                fbiSrf={sub.fbiSrf}
                                                stateSrf={sub.stateSrf}
                                                stateCaseType={sub.stateCaseType}
                                            />
                                        </TableCell>
                                        <TableCell className="hide-on-mobile">
                                            <span className={`status ${sub.state === 'COMPLETE' ? 'status-complete' : 'status-pending'}`}>
                                                {sub.state === 'COMPLETE' ? 'Complete' : 'Pending'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="hide-on-mobile">{new Date(sub.created).toLocaleString()}</TableCell>
                                        <TableCell className="hide-on-mobile">{sub.submittedBy}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={hasMore ? submissions.length + 1 : submissions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </>
    );
};

export default SubmissionsList;
